import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useTransition, animated, useSpring } from "@react-spring/web";
import { Image } from "react-bootstrap";

import Text from "src/components/text";
import Thumbnail from "src/components/thumbnail";
import Button from "src/components/button";
import StepLoader from "../components/stepLoader";
import ModifyComponent from "../components/modify";
import { TabBar } from "src/components/tabbar";
import { DirectionalTabTransition } from "../components/directionalTabBar";
import { AssetCard } from "../components/assetCard";
import { ActionBox } from "../components/actionBox";
import { HistoryScreen } from "../components/history";
import {
  deleteMicroLearning,
  fetchMicroLearning,
  fetchMicroLearnings,
  generateArticle,
  invalidateMicroLearningDeletion,
} from "src/redux/actions/coaching/microLearning";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ReactMarkdown from "react-markdown";
import { MicroLearningContext, MicroLearningContextProps } from "../context";
import { Article, MicroLearning } from "src/types/coaching/microLearning";
import Actions from "src/components/actionbar";
import Slider from "src/components/slider";

import { AddAssetsDialog } from "src/components/asset/add";
import { FileService } from "src/utils/file.service";
import Tooltip from "src/components/tooltip";
import { toast } from "react-toastify";

import _, { set } from "lodash";
import cx from "classnames";
import styles from "./preview.module.scss";

const PreviewMicroLearningPage: React.FC = () => {
  const dispatch = useDispatch();

  const params = useParams();
  const microLearningId = params.id;

  const { loading, loaded, microLearning } = useSelector((state: any) => {
    const microLearningState = _.get(
      state.coaching,
      `microLearnings.item.${microLearningId}`,
    );
    const microLearning = _.get(microLearningState, `item`, null);
    const loading = _.get(microLearningState, "loading", true);
    const loaded = _.get(microLearningState, "loaded", false);
    return {
      loading,
      loaded,
      microLearning,
    };
  }, _.isEqual);

  const article = useMemo(() => {
    return microLearning?.article as Article;
  }, [microLearning]);

  const [microLearningContext, setMicroLearningContext] =
    useState<MicroLearningContextProps>({
      id: microLearningId,
      article: microLearning?.article,
      assets: microLearning?.assets || [],
      showHistory: false,
      expanded: false,
      showDelete: false,
    });

  const handleHistoryClick = () => {
    setMicroLearningContext((prev) => ({
      ...prev,
      showHistory: true,
    }));

    setMicroLearningContext((prev) => ({
      ...prev,
      expanded: false,
    }));
  };

  const handleExpand = () => {
    setMicroLearningContext((prev) => ({
      ...prev,
      expanded: !prev.expanded,
    }));
  };

  const handleDeleteClicked = () => {
    setMicroLearningContext((prev) => {
      return {
        ...prev,
        showDelete: !prev.showDelete,
      };
    });
  };

  useEffect(() => {
    if (microLearningId) {
      dispatch(fetchMicroLearning(microLearningId));
    }
  }, [microLearningId]);

  useEffect(() => {
    if (microLearning) {
      setMicroLearningContext((prev) => ({
        ...prev,
        article: microLearning?.article,
        assets: microLearning?.assets || [],
        onHistoryClick: handleHistoryClick,
        onExpand: handleExpand,
        onDeleteClick: handleDeleteClicked,
      }));
    }

    if (!_.isEmpty(article?.content)) {
      setMicroLearningContext((prev) => ({
        ...prev,
        expanded: true,
      }));
    }
  }, [microLearning]);

  if ((loading && !loaded) || !microLearning) {
    return <PreviewPagePlaceholder />;
  }

  return (
    <MicroLearningContext.Provider value={microLearningContext}>
      <PreviewMicroLearing microLearning={microLearning} />
    </MicroLearningContext.Provider>
  );
};

interface PreviewMicroLearningProps {
  microLearning: MicroLearning;
}

const PreviewMicroLearing: React.FC<PreviewMicroLearningProps> = ({
  microLearning,
}) => {
  {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const microLearningContext = React.useContext(MicroLearningContext);
    const showDeleteSlider = microLearningContext.showDelete;

    const articleContent = useMemo(() => {
      return _.get(microLearning, "article.content", "");
    }, [microLearning]);

    const articleGenerated = useSelector((state: any) => {
      return _.get(
        state.coaching.microLearnings,
        `item.${microLearning.id}.generateArticle.generated`,
        false,
      );
    });

    const articleError = _.get(microLearning, "article.error", null);
    const emptyContent = _.isEmpty(articleContent.trim());

    const processingArticle = useSelector((state: any) => {
      const microLearningState = _.get(
        state.coaching.microLearnings,
        `item.${microLearning.id}`,
      );
      const generating = _.get(
        microLearningState,
        `generateArticle.generating`,
        false,
      );
      const status = _.get(microLearningState, `item.article.status`, null);
      return (
        generating ||
        _.includes(["in_progress", "requested", "pending"], status)
      );
    });

    const [showSeek, setShowSeek] = useState(false);
    const [isModifyOpen, setIsModifyOpen] = useState(false);
    const [generating, setGenerating] = useState(processingArticle);
    const [showAddAssets, setShowAddAssets] = useState(false);
    const [assets, setAssets] = useState<any>(microLearning.assets || []);
    const [activeTab, setActiveTab] = useState<number>(0);

    const onExpandClick = () => {
      if (microLearningContext.onExpand) {
        microLearningContext.onExpand();
      }
    };

    const handleGenerateArticle = () => {
      dispatch(
        generateArticle({
          id: microLearning.id!,
          assets: assets.map((asset: any) => asset.id),
        }),
      );
    };

    const handleAddAsset = () => {
      setShowAddAssets(true);
    };

    const entityAction = (entity: any) => {
      if (entity.entityType === "asset") {
        const fileType = FileService.getFileType(entity.metadata);
        const allowedTypes = [
          "PDF",
          "PPT",
          "PPTX",
          "DOC",
          "DOCX",
          "GOOGLE_SLIDES",
          "LINK",
        ];
        const disabled = fileType && !_.includes(allowedTypes, fileType);
        return (
          <Tooltip
            content="Only pptx, pdf assets can be added to microlearning"
            placement="auto"
            disabled={!disabled}
          >
            <button className={styles.action}>
              <i
                className={cx(
                  styles.icon,
                  disabled ? "paperflite-lock" : "paperflite-add",
                )}
              ></i>
            </button>
          </Tooltip>
        );
      }
      return false;
    };

    const assetSelectionFilter = (entity: any) => {
      if (entity.entityType === "asset") {
        const fileType = FileService.getFileType(entity.metadata);
        const allowedTypes = [
          "PDF",
          "PPT",
          "PPTX",
          "DOC",
          "DOCX",
          "GOOGLE_SLIDES",
          "LINK",
        ];
        return _.includes(allowedTypes, fileType);
      }
      return false;
    };

    const handleFinishAddAssets = (assets: any[]) => {
      const invalidAssets = assets.filter((asset) => {
        const fileType = FileService.getFileType(asset.metadata);
        const allowedTypes = [
          "PDF",
          "PPT",
          "PPTX",
          "DOC",
          "DOCX",
          "GOOGLE_SLIDES",
          "LINK",
        ];
        return !_.includes(allowedTypes, fileType);
      });

      if (_.size(invalidAssets) > 0) {
        toast.error("Only pptx, pdf assets can be added to microlearning");
        return false;
      }

      const filteredAssets = assets.filter((asset) => {
        const fileType = FileService.getFileType(asset.metadata);
        const allowedTypes = [
          "PDF",
          "PPT",
          "PPTX",
          "DOC",
          "DOCX",
          "GOOGLE_SLIDES",
          "LINK",
        ];
        return _.includes(allowedTypes, fileType);
      });

      if (_.size(filteredAssets) === 0) {
        toast.error(
          "No valid assets selected. Please select pptx, pdf assets.",
        );
        return false;
      }

      if (_.size(filteredAssets) > 5) {
        toast.error(
          "You can only add a maximum of 5 assets to a microlearinig.",
        );
        return false;
      }

      if (filteredAssets.length > 0) {
        setAssets(filteredAssets);
        handleCloseAddAssets();
      }

      return true;
    };

    const handleCloseAddAssets = () => {
      setShowAddAssets(false);
    };

    useEffect(() => {
      setGenerating(processingArticle);
    }, [processingArticle]);

    useEffect(() => {
      if (articleGenerated && microLearning.id!) {
        dispatch(fetchMicroLearning(microLearning.id!)); 
      }
    }, [articleGenerated]);    

    const historySpring = useSpring({
      x: microLearningContext.showHistory ? 0 : 100,
      opacity: microLearningContext.showHistory ? 1 : 0,
      config: { tension: 210, friction: 32 },
    });

    const actionsSpring = useSpring({
      x:
        microLearningContext.showHistory ||
        showSeek ||
        microLearningContext.expanded
          ? 100
          : 0,
      opacity:
        microLearningContext.showHistory ||
        showSeek ||
        microLearningContext.expanded
          ? 0
          : 1,
      config: { tension: 210, friction: 32 },
    });

    const seekSpring = useSpring({
      x: showSeek ? 0 : 100,
      opacity: showSeek ? 1 : 0,
      config: { tension: 210, friction: 32 },
    });

    const modifyTransition = useTransition(isModifyOpen, {
      from: { opacity: 0, y: 40 },
      enter: { opacity: 1, y: 0 },
      leave: { opacity: 0, y: 40 },
      config: { tension: 210, friction: 26 },
    });

    const layoutSpring = useSpring({
      leftWidth: microLearningContext.expanded ? 90 : 70,
      rightWidth: microLearningContext.expanded ? 10 : 30,
      x: microLearningContext.expanded ? 0 : 100,
      display: microLearningContext.expanded ? "flex" : "none",
      opacity: microLearningContext.expanded ? 1 : 0,
      config: {
        tension: microLearningContext.expanded ? 210 : 0,
        friction: microLearningContext.expanded ? 28 : 0,
      },
    });

    const wrapperSpring = useSpring({
      width: microLearningContext.expanded ? "76.5%" : "100%",
      right: microLearningContext.expanded ? "10rem" : "0rem",
      position: "relative",
      config: {
        tension: 210,
        friction: 28,
      },
    });

    const buttonSpring = useSpring({
      width: microLearningContext.expanded ? 100 : 70,
      config: {
        tension: 210,
        friction: 28,
      },
    });

    return (
      <div className={styles.previewContainer}>
        <ArticleHeader />
        <div className={styles.articleWrapper}>
          <div className={styles.wrapper}>
            <span className={styles.heading}>{microLearning.title}</span>
            <TabBar
              gap={16}
              tabSize="equal"
              active={activeTab}
              onSelectionChange={(index) => {
                setActiveTab(index);
              }}
              innerClass={styles.tabInner}
              className={styles.tab}
            >
              <div
                className={cx(styles.tabItem, {
                  [styles.active]: activeTab === 0,
                })}
              >
                <i className={cx("paperflite-document", styles.document)} />
                <span>article</span>
              </div>

              <div
                className={cx(styles.tabItem, {
                  [styles.active]: activeTab === 1,
                })}
              >
                <i className="paperflite-file" />
                <span>sources</span>
              </div>
            </TabBar>
            {!articleError && (
              <div className={styles.main}>
                <animated.div
                  className={styles.left}
                  style={{
                    width: layoutSpring.leftWidth.to((w) => `${w}%`),
                  }}
                >
                  <div className={styles.wrapper}>
                    <ArticleContentContainer
                      generating={generating}
                      microLearning={microLearning}
                      activeTab={activeTab}
                      assets={assets}
                      handleGenerateArticle={handleGenerateArticle}
                      handleAddAsset={handleAddAsset}
                    />
                  </div>
                </animated.div>

                <DeleteMicrolearningDialog
                  show={showDeleteSlider}
                  microlearning={microLearning}
                />
              </div>
            )}

            <AddAssetsDialog
              show={showAddAssets}
              selectableEntityTypes={["asset"]}
              allowInternalEntities={true}
              allowBackgroundUpload={false}
              initialEntities={assets}
              entityAction={entityAction}
              selectionFilter={assetSelectionFilter}
              onFinish={handleFinishAddAssets}
              onClose={handleCloseAddAssets}
            />
            <Outlet />
          </div>
        </div>
      </div>
    );
  }
};

const ArticleHeader = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const microLearningContext = React.useContext(MicroLearningContext);

  const onHistoryClick = () => {
    if (microLearningContext.onHistoryClick) {
      microLearningContext.onHistoryClick();
    }
  };

  const onAddClick = () => {
    navigate("/coaching/microlearning");
  };

  const onDeleteClick = () => {
    microLearningContext.onDeleteClick?.();
  };

  return (
    <header className={styles.header}>
      <button
        className={cx(styles.left, styles.back)}
        onClick={() => {
          navigate("/coaching/microlearning");
          dispatch(
            fetchMicroLearnings({
              page: 0,
              limit: 10,
            }),
          );
        }}
      >
        <i className={cx("paperflite-left-arrow")} />
        <span>
          back to <q className={styles.explore}>microlearning</q>
        </span>
      </button>
      <div className={styles.right}>
        <Actions.IconButton
          icon="paperflite-delete"
          className={styles.tab}
          onClick={onDeleteClick}
          tooltip="delete microlearning"
        />
        <Actions.Button
          icon="paperflite-add"
          className={styles.add}
          title="start new learning"
          onClick={onAddClick}
        />
      </div>
    </header>
  );
};

interface ArticleContentContainerProps {
  generating: boolean;
  microLearning: MicroLearning;
  activeTab: any;
  assets: any[];
  handleGenerateArticle: () => void;
  handleAddAsset: () => void;
}

const ArticleContentContainer: React.FC<ArticleContentContainerProps> = ({
  generating,
  microLearning,
  activeTab,
  assets,
  handleGenerateArticle,
  handleAddAsset,
}) => {
  const tabKeys = ["article", "sources"] as const;
  const { content, citations } = useMemo(() => {
    return {
      content: _.get(microLearning, "article.content", ""),
      citations: _.get(microLearning, "article.citations", []),
    };
  }, [microLearning]);

  const articleContent = useMemo(() => {
    return _.get(microLearning, "article.content", "");
  }, [microLearning]);

  const articleError = _.get(microLearning, "article.error", null);
  const emptyContent = _.isEmpty(articleContent.trim());

  const progressTransition = useTransition(generating, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  return (
    <section className={styles.articleContent}>
      <div className={styles.contentWrapper}>
        <TabSection index={0} selected={activeTab}>
          {!articleContent && generating && (
            <div className={styles.processingContainer}>
              <h1 className={styles.title}>
                generating&nbsp;<u>article</u>
                <img src="/images/coaching/dots_loading.gif" />
              </h1>
              <p className={styles.description}>
                We’re analyzing your content and creating a structured, bite-sized article. <br />
                This may take a few minutes. Feel free to go back—we&apos;ll
                notify you once it&apos;s ready.
              </p>
            </div>
          )}
          {(articleError || emptyContent) && !generating && (
            <div className={styles.errorContainer}>
              <h1 className={styles.title}>article generation failed</h1>
              <p className={styles.description}>
                {articleError?.code ===
                  "article.generation.insufficient_content" &&
                  "this usually occurs when the asset doesn't have enough content in them..."}

                {!articleError?.code &&
                  "something went wrong while generating the article. please try again."}
              </p>
              <div className={styles.actions}>
                <Button
                  variant="courses-primary"
                  className={styles.generateButton}
                  text="retry"
                  icon="paperflite-reverse"
                  onClick={handleGenerateArticle}
                />

                {(articleError?.code ===
                  "article.generation.insufficient_content" ||
                  _.isEmpty(articleContent.trim())) && (
                  <Button
                    variant="secondary"
                    className={styles.uploadButton}
                    text="upload new assets"
                    icon="paperflite-upload"
                    onClick={handleAddAsset}
                  />
                )}
              </div>
            </div>
          )}
          {!articleError && !emptyContent && (
            <Markdown
              rehypePlugins={[rehypeRaw]}
              remarkPlugins={[remarkGfm]}
              components={{
                cite: ({ node, ...props }) => {
                  const id = props["id"];
                  const citation = citations.find((c: any) => c.id == id);
                  if (!citation) {
                    return (
                      <span
                        // onClick={() => openCitation(citation)}
                        style={{
                          cursor: "pointer",
                          color: "#2563eb",
                        }}
                      >
                        [{id}]
                      </span>
                    );
                  }

                  return (
                    <span
                      // onClick={() => openCitation(citation)}
                      style={{
                        cursor: "pointer",
                        color: "#2563eb",
                      }}
                    >
                      [{citation.text || ""}]
                    </span>
                  );
                },
              }}
            >
              {content}
            </Markdown>
          )}
        </TabSection>
        <TabSection index={1} selected={activeTab}>
          <div className={styles.assets}>
            {(microLearning.assets || []).map((asset) => (
              <AssetCard key={asset.id} asset={asset} />
            ))}
          </div>
        </TabSection>
      </div>
    </section>
  );
};

interface TabSectionProps {
  index: number;
  selected: number;
  children: ReactNode;
}

const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;

  const fromTransform =
    index === 0 ? `translate3d(-100%, 0, 0)` : `translate3d(100%, 0, 0)`;

  const [animation] = useSpring(
    {
      height: active ? "auto" : 0,
      opacity: active ? 1 : 0,
      transform: active ? `translate3d(0, 0, 0)` : fromTransform,
      config: { tension: 240, friction: 25 },
    },
    [active],
  );

  return (
    <animated.div
      style={animation}
      className={cx(styles.tabBody, styles.stack)}
    >
      {active && children}
    </animated.div>
  );
};
interface DeleteMicrolearningDialogProps {
  show?: boolean;
  microlearning: MicroLearning;
  onFinish?: () => void;
  onClose?: () => void;
}

export const DeleteMicrolearningDialog: React.FC<
  DeleteMicrolearningDialogProps
> = ({ show = false, microlearning, onFinish, onClose = () => {} }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const microLearningContext = React.useContext(MicroLearningContext);

  const microLearningState = useSelector(
    (state: any) => _.get(state.coaching, "microLearnings"),
    _.isEqual,
  );

  const id = _.get(microlearning, "id");

  const deleted = _.get(microLearningState, `deletion.${id}.deleted`, false);
  const deleting = _.get(microLearningState, `deletion.${id}.deleting`, false);

  useEffect(() => {
    if (deleted) {
      microLearningContext.onDeleteClick?.();
      navigate("/coaching/microlearning");
      dispatch(invalidateMicroLearningDeletion());
    }
  }, [deleted]);

  const handleConfirm = () => {
    dispatch(deleteMicroLearning(microlearning.id!));
  };

  const handleClose = () => {
    microLearningContext.onDeleteClick?.();
  };

  return (
    <Slider.Dialog width={"400px"} show={show} onClose={handleClose}>
      <Slider.Header title="deleting..." message={microlearning.title} />
      <Slider.Body>
        <div className={styles.deleteBody}>
          <div className={styles.message}>
            Are you sure you want to delete this microlearning? This action
            cannot be undone.
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          disabled={deleting}
          testId="delete-course-confirm"
          onClick={handleConfirm}
        >
          {deleting ? "Deleting..." : "Confirm"}
        </Button>
        <Button
          disabled={deleting}
          onClick={handleClose}
          testId="delete-course-cancel"
        >
          Cancel
        </Button>
      </Slider.Actions>
    </Slider.Dialog>
  );
};

export const PreviewPagePlaceholder = () => {
  return (
    <div className={styles.previewPlaceholderComponent}>
      <ArticleHeader />
      <div className={cx(styles.previewPlaceholder, styles.placeholder)}>
        <div className={cx(styles.block, styles.heading)} />

        <div className={styles.tabs}>
          <div className={cx(styles.tab)}>
            <div className={cx(styles.block, styles.square)}></div>
            <div className={cx(styles.block, styles.label)}></div>
          </div>
          <div className={cx(styles.tab)}>
            <div className={cx(styles.block, styles.square)}></div>
            <div className={cx(styles.block, styles.label)}></div>
          </div>
        </div>

        <div className={styles.subHeading}>
          <div className={cx(styles.block, styles.subLineOne)} />
          <div className={cx(styles.block, styles.subLineTwo)} />
          <div className={cx(styles.block, styles.subLineThree)} />
        </div>

        <div className={cx(styles.block, styles.thumbnail)} />

        <div className={styles.content}>
          <div className={cx(styles.block, styles.lineOne)} />
          <div className={cx(styles.block, styles.lineTwo)} />
          <div className={cx(styles.block, styles.lineThree)} />
        </div>
      </div>
    </div>
  );
};

export default PreviewMicroLearningPage;
